import React, { useEffect, useState } from "react";
import { Distribution, TaxUnit } from "../../../../../models";
import { toast } from "react-toastify";
import { cancelAutoGiroAgreement, cancelAvtaleGiroAgreement, cancelVippsAgreement, updateAutoGiroAgreement, updateAvtaleagreementAmount, updateAvtaleagreementPaymentDay, updateAvtalegiroAgreementDistribution, updateVippsAgreementDay, updateVippsAgreementDistribution, updateVippsAgreementPrice } from "./_queries";
import { useAuth0 } from "@auth0/auth0-react";
import { getAccessToken } from "../../../../../hooks/useApi";
import { useSWRConfig } from "swr";
import { AlertCircle, Check, Info } from "react-feather";
import style from "./AgreementDetails.module.scss";
import { EffektButton, EffektButtonVariant } from "../../../../shared/components/EffektButton/EffektButton";
import { Lightbox } from "../../../../shared/components/Lightbox/Lightbox";
import { checkPaymentDate } from "../../../../../util/dates";
import { getUserId } from "../../../../../lib/user";
import { AgreementSingleCauseAreaDetails } from "./singleCauseAreaDetails/AgreementSingleCauseAreaDetails";
import { useCauseAreas } from "../../../../../_queries";
import { AgreementMultipleCauseAreaDetails, AgreementMultipleCauseAreaDetailsConfiguration } from "./multipleCauseAreasDetails/AgreementMultipleCauseAreasDetails";
import { DatePickerInputConfiguration } from "../../../../shared/components/DatePicker/DatePickerInput";
import { AgreementTypes } from "./AgreementList";
import { getStandardOrganizationId, getStandardOrganizationIds, hydrateDistributionAmounts, prepareDistributionForSave, setStandardCauseAreaAmount } from "../../distributionAmounts";
import { useMainLocale } from "../../../../../context/MainLocaleContext";
export type AgreementDetailsConfiguration = {
  save_button_text: string;
  cancel_button_text: string;
  membership_label_prefix?: string;
  date_selector_configuration: DatePickerInputConfiguration;
  loading_text: string;
  error_text: string;
  toasts_configuration: {
    success_text: string;
    failure_text: string;
    no_changes_text: string;
  };
  agreement_cancel_lightbox: {
    title: string;
    text: string;
    text_manual?: string;
    withdrawal_warning_text: string;
  };
  agreement_cancelled_lightbox: {
    title: string;
    text: string;
    lightbox_button_text: string;
  };
  distribution_configuration: AgreementMultipleCauseAreaDetailsConfiguration;
};
export const AgreementDetails: React.FC<{
  type: AgreementTypes;
  agreementId: string;
  agreementKid: string;
  inputSum: number;
  inputDate: number;
  inputDistribution: Distribution;
  taxUnits: TaxUnit[];
  endpoint: string;
  agreementCancelled: (type: AgreementTypes, agreementId: string, agreementKid: string) => void;
  configuration: AgreementDetailsConfiguration;
}> = ({
  type,
  agreementId,
  agreementKid,
  inputSum,
  inputDate,
  inputDistribution,
  taxUnits,
  endpoint,
  agreementCancelled,
  configuration
}) => {
  const {
    getAccessTokenSilently,
    user
  } = useAuth0();
  const {
    mutate
  } = useSWRConfig();
  // Parse and stringify to make a deep copy of the object
  const [distribution, setDistribution] = useState<Distribution>(() => hydrateDistributionAmounts(inputDistribution, inputSum));
  const [lastSavedDistribution, setLastSavedDistribution] = useState<Distribution>(() => hydrateDistributionAmounts(inputDistribution, inputSum));
  const mainLocale = useMainLocale();
  const [day, setDay] = useState(inputDate);
  // Changing the payment date of AutoGiro agreements is not supported on the Swedish platform
  const dateDisabled = type === "AutoGiro" && mainLocale === "sv";
  const [sum, setSum] = useState(inputSum);
  useEffect(() => {
    setLastSavedDistribution(hydrateDistributionAmounts(inputDistribution, inputSum));
  }, [inputDistribution, inputSum]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const {
    data: systemCauseAreas,
    error: causeAreasError,
    loading: causeAreasLoading,
    isValidating: causeAreasIsValidating
  } = useCauseAreas(getAccessTokenSilently);
  useEffect(() => {
    // Check if the distribution has all the system cause areas
    // If not, add them
    if (systemCauseAreas && inputDistribution && inputDistribution.causeAreas) {
      const systemCauseAreaIds = systemCauseAreas.map(causeArea => causeArea.id);
      const distributionCauseAreaIds = inputDistribution.causeAreas.map(causeArea_0 => causeArea_0.id);
      const missingCauseAreas = systemCauseAreaIds.filter(id => !distributionCauseAreaIds.includes(id));
      if (missingCauseAreas.length > 0) {
        const newDistribution = hydrateDistributionAmounts(inputDistribution, inputSum);
        missingCauseAreas.forEach(id_0 => {
          const systemCauseArea = systemCauseAreas.find(causeArea_1 => causeArea_1.id === id_0);
          if (systemCauseArea) {
            newDistribution.causeAreas.push({
              id: systemCauseArea.id,
              name: systemCauseArea.name,
              standardSplit: true,
              percentageShare: "0",
              amount: 0,
              organizations: systemCauseArea.organizations.map(org => {
                return {
                  id: org.id,
                  percentageShare: org.standardShare?.toString() || "0",
                  amount: 0
                };
              })
            });
          }
        });
        setDistribution(newDistribution);
      }
    }
  }, [systemCauseAreas, inputDistribution, inputSum]);
  const changeSum = (nextSum: number) => {
    setDistribution(current => {
      const causeArea_2 = current.causeAreas[0];
      const systemCauseArea_0 = systemCauseAreas?.find(system => system.id === causeArea_2.id);
      const standardOrganizationId = systemCauseArea_0 ? getStandardOrganizationId(systemCauseArea_0) : undefined;
      if (standardOrganizationId === undefined) return current;
      return {
        ...current,
        causeAreas: [setStandardCauseAreaAmount(causeArea_2, nextSum, standardOrganizationId)]
      };
    });
    setSum(nextSum);
  };
  const save = async () => {
    const token = await getAccessToken(getAccessTokenSilently);
    const distributionChanged = JSON.stringify(distribution) !== JSON.stringify(lastSavedDistribution);
    const agreementSum = systemCauseAreas && (systemCauseAreas.length > 1 || !distribution.causeAreas[0]?.standardSplit) ? distribution.causeAreas.reduce((total, causeArea_3) => total + (causeArea_3.amount ?? 0), 0) : sum;
    const sumChanged = agreementSum !== inputSum;
    const dayChanged = day !== inputDate;
    if (!distributionChanged && !dayChanged && !sumChanged) {
      noChangesToast(configuration.toasts_configuration.no_changes_text);
      return;
    }
    if (!user) throw new Error("User is not logged in");
    let distributionPayload: Distribution;
    try {
      distributionPayload = prepareDistributionForSave(distribution, agreementSum, getStandardOrganizationIds(systemCauseAreas ?? []));
    } catch {
      failureToast(configuration.toasts_configuration.failure_text);
      return;
    }
    setLoadingChanges(true);
    if (type == "Vipps") {
      let result = null;
      if (sumChanged) {
        result = await updateVippsAgreementPrice(endpoint, agreementSum, token);
      }
      if (dayChanged) {
        result = await updateVippsAgreementDay(endpoint, day, token);
      }
      if (distributionChanged || sumChanged) {
        result = await updateVippsAgreementDistribution(endpoint, distributionPayload, token);
      }
      if (result != null) {
        successToast(configuration.toasts_configuration.success_text);
        mutate(`/donors/${getUserId(user)}/recurring/vipps/`);
        setLoadingChanges(false);
        setLastSavedDistribution(JSON.parse(JSON.stringify(distribution)));
      } else {
        failureToast(configuration.toasts_configuration.failure_text);
        setLoadingChanges(false);
      }
    } else if (type == "AvtaleGiro") {
      let result_0 = null;
      if (sumChanged) {
        result_0 = await updateAvtaleagreementAmount(endpoint, agreementSum * 100, token);
      }
      if (dayChanged) {
        result_0 = await updateAvtaleagreementPaymentDay(endpoint, day, token);
      }
      if (distributionChanged || sumChanged) {
        result_0 = await updateAvtalegiroAgreementDistribution(endpoint, distributionPayload, token);
      }
      if (result_0 !== null) {
        successToast(configuration.toasts_configuration.success_text);
        mutate(`/donors/${getUserId(user)}/recurring/avtalegiro/`);
        setLoadingChanges(false);
        setLastSavedDistribution(JSON.parse(JSON.stringify(distribution)));
      } else {
        failureToast(configuration.toasts_configuration.failure_text);
        setLoadingChanges(false);
      }
    } else if (type == "AutoGiro") {
      let result_1 = await updateAutoGiroAgreement(endpoint, distributionChanged || sumChanged ? distributionPayload : null, dayChanged && !dateDisabled ? day : null, sumChanged ? agreementSum : null, token);
      if (result_1 !== null) {
        successToast(configuration.toasts_configuration.success_text);
        mutate(`/donors/${getUserId(user)}/recurring/autogiro/`);
        setLoadingChanges(false);
        setLastSavedDistribution(JSON.parse(JSON.stringify(distribution)));
      } else {
        failureToast(configuration.toasts_configuration.failure_text);
        setLoadingChanges(false);
      }
    } else {
      failureToast("Uknown agreement type");
      setLoadingChanges(false);
    }
  };
  const cancel = async () => {
    if (!user) throw new Error("User is not logged in");
    setLightboxOpen(false);
    const token_0 = await getAccessToken(getAccessTokenSilently);
    if (type === "Vipps") {
      const cancelled = await cancelVippsAgreement(endpoint, token_0);
      if (cancelled) {
        successToast(configuration.toasts_configuration.success_text);
        agreementCancelled(type, agreementId, agreementKid);
        mutate(`/donors/${getUserId(user)}/recurring/vipps/`);
      } else {
        failureToast(configuration.toasts_configuration.failure_text);
      }
    } else if (type === "AvtaleGiro") {
      const cancelled_0 = await cancelAvtaleGiroAgreement(endpoint, token_0);
      if (cancelled_0) {
        successToast(configuration.toasts_configuration.success_text);
        agreementCancelled(type, agreementId, agreementKid);
        mutate(`/donors/${getUserId(user)}/recurring/avtalegiro/`);
      } else {
        failureToast(configuration.toasts_configuration.failure_text);
      }
    } else if (type === "AutoGiro") {
      const cancelled_1 = await cancelAutoGiroAgreement(endpoint, token_0);
      if (cancelled_1) {
        successToast(configuration.toasts_configuration.success_text);
        agreementCancelled(type, agreementId, agreementKid);
        mutate(`/donors/${getUserId(user)}/recurring/autogiro/`);
      } else {
        failureToast(configuration.toasts_configuration.failure_text);
      }
    } else {
      failureToast("Uknown agreement type");
    }
  };
  if (causeAreasLoading) {
    return <div className={style.errorWrapper}>
        <p>{configuration.loading_text}</p>
      </div>;
  } else if (causeAreasError) {
    return <div className={style.errorWrapper}>
        <p>{configuration.error_text}</p>
      </div>;
  } else if (!systemCauseAreas) {
    return <div className={style.errorWrapper}>
        <p>{configuration.error_text}</p>
      </div>;
  }
  if (!distribution.causeAreas || distribution.causeAreas.some(c => !c.organizations)) {
    return <div className={style.errorWrapper}>
        <p>{configuration.error_text}</p>
      </div>;
  } else {
    return <div className={style.wrapper} data-cy="agreement-list-details">
        {systemCauseAreas.length === 1 && <AgreementSingleCauseAreaDetails distribution={distribution} savedDistribution={lastSavedDistribution} setDistribution={setDistribution} day={day} setDay={setDay} sum={sum} setSum={setSum} onSumChange={changeSum} taxUnits={taxUnits} dateSelectorConfig={configuration.date_selector_configuration} dateDisabled={dateDisabled}></AgreementSingleCauseAreaDetails>}

        {systemCauseAreas.length > 1 && <AgreementMultipleCauseAreaDetails systemCauseAreas={systemCauseAreas} distribution={distribution} savedDistribution={lastSavedDistribution} setDistribution={setDistribution} day={day} setDay={setDay} taxUnits={taxUnits} configuration={configuration.distribution_configuration} dateSelectorConfig={configuration.date_selector_configuration} dateDisabled={dateDisabled}></AgreementMultipleCauseAreaDetails>}

        <div className={style.actions}>
          <EffektButton variant={EffektButtonVariant.SECONDARY} onClick={() => setLightboxOpen(true)} cy="btn-cancel-agreement">
            {configuration.cancel_button_text}
          </EffektButton>
          <EffektButton onClick={() => save()} disabled={loadingChanges} cy="btn-save-agreement">
            {!loadingChanges ? configuration.save_button_text : configuration.loading_text}
          </EffektButton>
        </div>

        <Lightbox open={lightboxOpen} onConfirm={() => cancel()} onCancel={() => setLightboxOpen(false)}>
          <div className={style.textWrapper}>
            <h5>{configuration.agreement_cancel_lightbox.title}</h5>
            <p>{configuration.agreement_cancel_lightbox.text}</p>
            {checkPaymentDate(new Date(), day) && type === "AvtaleGiro" ? <p>{configuration.agreement_cancel_lightbox.withdrawal_warning_text}</p> : null}
          </div>
        </Lightbox>
      </div>;
  }
};
const successToast = (text: string) => toast.success(text, {
  icon: <Check size={24} color={"black"} />
});
const failureToast = (text: string) => toast.error(text, {
  icon: <AlertCircle size={24} color={"black"} />
});
const noChangesToast = (text: string) => toast.error(text, {
  icon: <Info size={24} color={"black"} />
});